/**
 * Column header menu: sort (ascending / descending / off) and every filter
 * facet of the column — a value checklist with counts for enums and catalog
 * stats, a min/max range for numbers, a "contains" box for text. Changes apply
 * instantly (the whole event is in memory), so there is no Apply button.
 */
import { useId, useRef, useState } from 'react';

import type { PlayView } from '../../lib/api-types';

import type { ArchiveColumn, FilterFacet } from './archive-columns';
import {
  facetOptions,
  filterPlays,
  toggleEnumValue,
  withFilter,
  type ArchiveQuery,
  type FacetFilter,
} from './archive-query';
import { Popover } from './popover';

const SEARCHABLE_OPTION_COUNT = 9;

function EnumFacet({
  facet,
  filter,
  plays,
  query,
  onChange,
}: {
  facet: FilterFacet;
  filter: FacetFilter | undefined;
  plays: readonly PlayView[];
  query: ArchiveQuery;
  onChange: (next: ArchiveQuery) => void;
}): JSX.Element {
  const [needle, setNeedle] = useState('');
  const selected = filter?.kind === 'enum' ? filter.values : [];
  const options = facetOptions(facet, plays, selected);
  const shown =
    needle.trim() === ''
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().includes(needle.trim().toLowerCase())
        );
  const lowerSelected = selected.map((value) => value.toLowerCase());
  return (
    <div className="colmenu__facet">
      {options.length >= SEARCHABLE_OPTION_COUNT ? (
        <input
          type="search"
          className="colmenu__search"
          placeholder={`Find ${facet.label.toLowerCase()}…`}
          aria-label={`Find a ${facet.label} value`}
          value={needle}
          onChange={(event) => setNeedle(event.target.value)}
        />
      ) : null}
      <ul className="colmenu__options" aria-label={`${facet.label} values`}>
        {shown.length === 0 ? <li className="colmenu__empty">No values</li> : null}
        {shown.map((option) => {
          const isChecked = lowerSelected.includes(option.value.toLowerCase());
          return (
            <li key={option.value}>
              <label
                className={`colmenu__option${option.count === 0 && !isChecked ? ' colmenu__option--empty' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onChange(toggleEnumValue(query, facet.id, option.value))}
                />
                <span className="colmenu__option-label">{option.label}</span>
                <span className="colmenu__count">{option.count}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RangeFacet({
  facet,
  filter,
  query,
  onChange,
}: {
  facet: FilterFacet;
  filter: FacetFilter | undefined;
  query: ArchiveQuery;
  onChange: (next: ArchiveQuery) => void;
}): JSX.Element {
  const id = useId();
  const range =
    filter?.kind === 'range' ? filter : { kind: 'range' as const, min: null, max: null };
  const set = (key: 'min' | 'max', raw: string): void => {
    const trimmed = raw.trim();
    const value =
      trimmed === '' || trimmed === '-' || !Number.isFinite(Number(trimmed))
        ? null
        : Number(trimmed);
    onChange(withFilter(query, facet.id, { ...range, [key]: value }));
  };
  return (
    <div className="colmenu__range">
      <label htmlFor={`${id}-min`}>Min</label>
      <input
        id={`${id}-min`}
        type="number"
        inputMode="numeric"
        min={facet.min}
        max={facet.max}
        value={range.min ?? ''}
        onChange={(event) => set('min', event.target.value)}
      />
      <label htmlFor={`${id}-max`}>Max</label>
      <input
        id={`${id}-max`}
        type="number"
        inputMode="numeric"
        min={facet.min}
        max={facet.max}
        value={range.max ?? ''}
        onChange={(event) => set('max', event.target.value)}
      />
      <span className="colmenu__range-hint">
        Applies as you type. Leave a side blank for open-ended.
      </span>
    </div>
  );
}

function TextFacet({
  facet,
  filter,
  query,
  onChange,
}: {
  facet: FilterFacet;
  filter: FacetFilter | undefined;
  query: ArchiveQuery;
  onChange: (next: ArchiveQuery) => void;
}): JSX.Element {
  return (
    <input
      type="search"
      className="colmenu__search"
      placeholder="Contains…"
      aria-label={`${facet.label} contains`}
      value={filter?.kind === 'text' ? filter.text : ''}
      onChange={(event) =>
        onChange(withFilter(query, facet.id, { kind: 'text', text: event.target.value }))
      }
    />
  );
}

export interface ColumnMenuProps {
  column: ArchiveColumn;
  query: ArchiveQuery;
  onChange: (next: ArchiveQuery) => void;
  /** Every play in the event (counts are taken against the rest of the query). */
  allPlays: readonly PlayView[];
  facets: ReadonlyMap<string, { facet: FilterFacet; column: ArchiveColumn }>;
  columns: readonly ArchiveColumn[];
  onHide?: () => void;
}

/** Sort buttons + every facet of one column. Used in the header popover and the Filter tool. */
export function ColumnMenuBody({
  column,
  query,
  onChange,
  allPlays,
  facets,
  columns,
}: Omit<ColumnMenuProps, 'onHide'>): JSX.Element {
  const sortDir = query.sort === column.id ? query.dir : null;
  return (
    <>
      <div className="colmenu__sort" role="group" aria-label={`Sort by ${column.title}`}>
        <button
          type="button"
          aria-pressed={sortDir === 'asc'}
          onClick={() => onChange({ ...query, sort: column.id, dir: 'asc' })}
        >
          ▲ Ascending
        </button>
        <button
          type="button"
          aria-pressed={sortDir === 'desc'}
          onClick={() => onChange({ ...query, sort: column.id, dir: 'desc' })}
        >
          ▼ Descending
        </button>
        {sortDir === null ? null : (
          <button type="button" onClick={() => onChange({ ...query, sort: null, dir: 'asc' })}>
            Clear
          </button>
        )}
      </div>
      <div className="colmenu__body">
        {column.facets
          .filter((facet) => facet.id !== 'unit')
          .map((facet) => {
            const filter = query.filters[facet.id];
            const scope = filterPlays(allPlays, query, columns, facets, facet.id);
            return (
              <section
                key={facet.id}
                className="colmenu__section"
                aria-label={`Filter ${facet.label}`}
              >
                <div className="colmenu__section-head">
                  <span>{column.facets.length > 1 ? facet.label : 'Filter'}</span>
                  {filter === undefined ? null : (
                    <button
                      type="button"
                      className="colmenu__clear"
                      onClick={() => onChange(withFilter(query, facet.id, null))}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {facet.kind === 'enum' ? (
                  <EnumFacet
                    facet={facet}
                    filter={filter}
                    plays={scope}
                    query={query}
                    onChange={onChange}
                  />
                ) : facet.kind === 'range' ? (
                  <RangeFacet facet={facet} filter={filter} query={query} onChange={onChange} />
                ) : (
                  <TextFacet facet={facet} filter={filter} query={query} onChange={onChange} />
                )}
              </section>
            );
          })}
      </div>
    </>
  );
}

/** The header button (label + sort arrow + filter dot) and its menu. */
export function ColumnHeader({
  column,
  query,
  onChange,
  allPlays,
  facets,
  columns,
  onHide,
}: ColumnMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const activeFacets = column.facets.filter((facet) => query.filters[facet.id] !== undefined);
  const sortDir = query.sort === column.id ? query.dir : null;
  const ariaSort = sortDir === null ? 'none' : sortDir === 'asc' ? 'ascending' : 'descending';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`colhead colhead--${column.align}${activeFacets.length > 0 ? ' colhead--filtered' : ''}${
          sortDir === null ? '' : ' colhead--sorted'
        }`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`${column.title}${sortDir === null ? '' : `, sorted ${ariaSort}`}${
          activeFacets.length > 0 ? ', filtered' : ''
        }`}
        title={`${column.title} — sort and filter`}
        data-sort={ariaSort}
        data-testid={`colhead-${column.id}`}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="colhead__text">
          {column.kicker === undefined ? null : (
            <span className="colhead__kicker">{column.kicker}</span>
          )}
          <span className="colhead__label">{column.header}</span>
        </span>
        {sortDir === null ? null : (
          <span className="colhead__sort" aria-hidden="true">
            {sortDir === 'asc' ? '▲' : '▼'}
          </span>
        )}
        {activeFacets.length > 0 ? <span className="colhead__dot" aria-hidden="true" /> : null}
      </button>
      <Popover
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        label={`${column.title} column`}
        className="colmenu"
        width={272}
        testId={`colmenu-${column.id}`}
      >
        <div className="colmenu__head">
          <span className="colmenu__title">{column.title}</span>
        </div>
        <ColumnMenuBody
          column={column}
          query={query}
          onChange={onChange}
          allPlays={allPlays}
          facets={facets}
          columns={columns}
        />
        {onHide === undefined || column.isPinned === true ? null : (
          <div className="colmenu__foot">
            <button
              type="button"
              className="colmenu__hide"
              onClick={() => {
                setIsOpen(false);
                onHide();
              }}
            >
              Hide column
            </button>
          </div>
        )}
      </Popover>
    </>
  );
}

/**
 * Toolbar "Sort & filter": pick ANY column (including ones hidden or dropped
 * for width, and on a phone where there is no header row) and sort / filter it.
 */
export function FilterTool({
  columns,
  query,
  onChange,
  allPlays,
  facets,
}: {
  columns: readonly ArchiveColumn[];
  query: ArchiveQuery;
  onChange: (next: ArchiveQuery) => void;
  allPlays: readonly PlayView[];
  facets: ReadonlyMap<string, { facet: FilterFacet; column: ArchiveColumn }>;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [columnId, setColumnId] = useState<string>(columns[0]?.id ?? '');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const choices = columns.filter((column) => column.facets.some((facet) => facet.id !== 'unit'));
  const column = choices.find((entry) => entry.id === columnId) ?? choices[0];
  const activeCount = Object.keys(query.filters).length + (query.sort === null ? 0 : 1);
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="archive-tool"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-testid="filter-tool-trigger"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span aria-hidden="true">⚲</span> Sort &amp; filter
        {activeCount > 0 ? <span className="archive-tool__count">{activeCount}</span> : null}
      </button>
      <Popover
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        label="Sort and filter"
        className="colmenu"
        width={300}
        align="end"
        testId="filter-tool"
      >
        <div className="colmenu__head">
          <label className="colmenu__title" htmlFor="filter-tool-column">
            Column
          </label>
          <select
            id="filter-tool-column"
            className="colmenu__select"
            value={column?.id ?? ''}
            onChange={(event) => setColumnId(event.target.value)}
          >
            {choices.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.title}
                {entry.facets.some((facet) => query.filters[facet.id] !== undefined) ? ' •' : ''}
              </option>
            ))}
          </select>
        </div>
        {column === undefined ? null : (
          <ColumnMenuBody
            key={column.id}
            column={column}
            query={query}
            onChange={onChange}
            allPlays={allPlays}
            facets={facets}
            columns={columns}
          />
        )}
      </Popover>
    </>
  );
}
